import React, { useEffect, useMemo, useRef, useState } from "react";
import "./WeatherWidget.css";
import { getApiBase } from "../../api/healthThreatsApi";

// Uses UTH API as a proxy for Nominatim (avoids CORS) + calls Open-Meteo directly.
const API_BASE = getApiBase();
const OM_BASE = "https://api.open-meteo.com/v1/forecast";

const readCountry = () => localStorage.getItem("country") || "";

async function fetchJSON(url, opts) {
  const r = await fetch(url, {
    ...opts,
    headers: {
      Accept: "application/json",
      ...(opts?.headers || {}),
    },
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

// --- Nominatim via UTH API proxy (NO direct calls to openstreetmap from browser) ---
async function nominatimSearch(q, countryCode) {
  const params = new URLSearchParams({
    q,
    format: "json",
    addressdetails: "1",
    limit: "6",
  });
  if (countryCode) params.set("countrycodes", countryCode.toLowerCase());

  // Your UTH API must expose: GET /geo/nominatim/search
  const url = `${API_BASE}/geo/nominatim/search?${params.toString()}`;
  return fetchJSON(url);
}

// Country capital via UTH API proxy.
async function getCapital(countryCode) {
  if (!countryCode) return null;

  const data = await fetchJSON(
    `${API_BASE}/geo/countries/capital/${encodeURIComponent(countryCode)}`
  );

  return data?.capital || null;
}

// Open-Meteo (no key) – direct call
async function getCurrentWeather(lat, lon) {
  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lon),
    current:
      "temperature_2m,relative_humidity_2m,apparent_temperature,weather_code,wind_speed_10m",
  });
  const url = `${OM_BASE}?${params.toString()}`;
  return fetchJSON(url);
}

function codeToText(code) {
  const map = {
    0: "Clear",
    1: "Mainly clear",
    2: "Partly cloudy",
    3: "Overcast",
    45: "Fog",
    48: "Rime fog",
    51: "Light drizzle",
    53: "Drizzle",
    55: "Dense drizzle",
    61: "Light rain",
    63: "Rain",
    65: "Heavy rain",
    71: "Light snow",
    73: "Snow",
    75: "Heavy snow",
    80: "Rain showers",
    81: "Rain showers",
    82: "Violent showers",
    95: "Thunderstorm",
    96: "Thunderstorm + hail",
    99: "Thunderstorm + hail",
  };
  return map[code] || `Code ${code}`;
}

export default function WeatherWidget() {
  const [status, setStatus] = useState("idle"); // idle|loading|ready|error
  const [err, setErr] = useState("");
  const [label, setLabel] = useState("—");
  const [weather, setWeather] = useState(null);

  const [countryCode, setCountryCode] = useState(readCountry());

  const [search, setSearch] = useState("");
  const [sug, setSug] = useState([]);
  const [open, setOpen] = useState(false);

  const debRef = useRef(null);
  const blurRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => {
      const c = readCountry();
      setCountryCode((p) => (p === c ? p : c));
    }, 1500);
    return () => clearInterval(t);
  }, []);

  const loadLatLon = async (lat, lon, placeLabel) => {
    setStatus("loading");
    setErr("");
    try {
      const w = await getCurrentWeather(lat, lon);
      setWeather(w?.current || null);
      setLabel(placeLabel || "Selected location");
      setStatus("ready");
    } catch {
      setStatus("error");
      setErr("Could not fetch weather.");
    }
  };

  const loadCapitalFallback = async () => {
    setOpen(false);
    setStatus("loading");
    setErr("");

    try {
      const capital = await getCapital(countryCode);
      if (!capital) {
        setStatus("error");
        setErr("No country selected for capital fallback.");
        return;
      }

      const list = await nominatimSearch(capital, countryCode);
      const best = list?.[0];
      if (!best) {
        setStatus("error");
        setErr("Could not find capital location.");
        return;
      }

      await loadLatLon(
        parseFloat(best.lat),
        parseFloat(best.lon),
        `${capital}${countryCode ? `, ${countryCode}` : ""}`
      );
    } catch {
      setStatus("error");
      setErr("Capital fallback failed.");
    }
  };

  useEffect(() => {
    loadCapitalFallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode]);

  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) {
      setSug([]);
      return;
    }

    clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      try {
        const list = await nominatimSearch(q, countryCode);
        setSug(Array.isArray(list) ? list : []);
      } catch {
        setSug([]);
      }
    }, 350);

    return () => clearTimeout(debRef.current);
  }, [search, countryCode]);

  const pick = async (s) => {
    setOpen(false);
    setSearch("");
    const name =
      s?.display_name?.split(",").slice(0, 2).join(", ") || "Selected location";
    await loadLatLon(parseFloat(s.lat), parseFloat(s.lon), name);
  };

  const temp = weather?.temperature_2m;
  const feels = weather?.apparent_temperature;
  const hum = weather?.relative_humidity_2m;
  const wind = weather?.wind_speed_10m;
  const code = weather?.weather_code;

  const desc = useMemo(() => {
    if (typeof code !== "number") return "";
    return codeToText(code);
  }, [code]);

  return (
    <div className="ww">
      <div className="ww-head">
        <div className="ww-title">Weather</div>
        <div className="ww-sub">{label}</div>
      </div>

      <div className="ww-actions">
        <button
          className="ww-btn ww-btn-ghost"
          type="button"
          onClick={loadCapitalFallback}
        >
          Use capital
        </button>
      </div>

      <div className="ww-search">
        <input
          className="ww-input"
          value={search}
          placeholder="Search city"
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            clearTimeout(blurRef.current);
            blurRef.current = setTimeout(() => setOpen(false), 120);
          }}
        />

        {open && sug.length > 0 && (
          <div className="ww-sug">
            {sug.map((s, i) => (
              <button
                key={`${s.place_id}-${i}`}
                className="ww-sug-item"
                type="button"
                onClick={() => pick(s)}
              >
                {s.display_name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="ww-body">
        {status === "loading" && <div className="ww-muted">Loading…</div>}

        {status === "error" && (
          <div className="ww-error">
            {err || "Error"}
          </div>
        )}

        {status === "ready" && weather && (
          <div className="ww-row">
            <div className="ww-left">
              <div className="ww-temp">
                {typeof temp === "number" ? `${Math.round(temp)}°C` : "—"}
              </div>
              <div className="ww-desc">{desc}</div>
              <div className="ww-meta">
                <span>
                  Feels:{" "}
                  {typeof feels === "number" ? `${Math.round(feels)}°C` : "—"}
                </span>
                <span>
                  Humidity: {typeof hum === "number" ? `${hum}%` : "—"}
                </span>
                <span>
                  Wind:{" "}
                  {typeof wind === "number" ? `${Math.round(wind)} km/h` : "—"}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}